import { useEffect, type ReactNode } from 'react'
import { useSession, type SessionState } from '@stagepost/web/app/useSession'
import { RouteLink } from '@stagepost/web/app/RouteLink'
import { Mark, PrimaryAction } from '@stagepost/web/components/primitives'
import { PRIVACY, TERMS, type LegalDocument } from './content'
import '@stagepost/web/styles/public-page.css'
import './legal.css'

/**
 * The two pages the marketing footer promises, at `/privacy` and `/terms`.
 *
 * They exist because the page told visitors we treat a room as private data and
 * then offered nothing behind the claim, and because GitHub and Google both
 * expect a published policy from anything signing their accounts in. A product
 * that asks for an OAuth grant and has no policy page is asking to be taken on
 * faith.
 *
 * Public in the same sense the guide is: above the loading gate in `App`, so
 * they paint whether or not the server can say who you are. Somebody reading a
 * privacy policy is frequently somebody deciding whether to sign up at all, and
 * a policy that waits on a session probe is one that fails for exactly them.
 *
 * The content lives in `content.tsx` and is deliberately narrow. Every factual
 * claim in it is one this codebase can be read to confirm: what is stored, what
 * the retention default is, which third parties see anything. Nothing in there
 * describes a practice the product does not have.
 */
export function Legal({ document: doc, session }: { document: 'privacy' | 'terms'; session: SessionState['status'] }) {
  const content = doc === 'privacy' ? PRIVACY : TERMS

  useEffect(() => {
    window.document.title = `${content.title} · stagepost`
  }, [content.title])

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: 'var(--app-height)', overflowX: 'hidden' }}>
      <div className="sp-marketing-glow" />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header label={content.title} session={session} />

        <main className="sp-legal">
          <h1
            style={{
              margin: '0 0 16px',
              fontSize: 'clamp(30px,4.4vw,50px)',
              lineHeight: 1.03,
              fontWeight: 800,
              letterSpacing: '-.045em',
              textWrap: 'balance',
            }}
          >
            {content.title}
          </h1>

          <p
            style={{
              margin: '0 0 10px',
              fontSize: 'clamp(15.5px,1.5vw,18px)',
              lineHeight: 1.6,
              color: 'var(--text-soft)',
              maxWidth: 620,
              textWrap: 'pretty',
            }}
          >
            {content.summary}
          </p>

          <div style={{ font: '400 11px var(--font-machine)', color: 'var(--text-faint)' }}>
            Last updated {content.updated}
          </div>

          {content.sections.map((section) => (
            <section key={section.heading} className="sp-legal-section">
              <h2
                style={{
                  margin: '0 0 14px',
                  fontSize: 'clamp(20px,2.2vw,26px)',
                  lineHeight: 1.16,
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                }}
              >
                {section.heading}
              </h2>
              {section.body.map((block, index) => (
                <Block key={index} block={block} />
              ))}
            </section>
          ))}

          <Closing />
        </main>

        <Footer />
      </div>
    </div>
  )
}

function Block({ block }: { block: LegalDocument['sections'][number]['body'][number] }) {
  if (typeof block === 'string') {
    return <p className="sp-legal-paragraph">{block}</p>
  }
  return (
    <ul className="sp-legal-list">
      {block.list.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  )
}

function Header({ label, session }: { label: string; session: SessionState['status'] }) {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        backdropFilter: 'blur(18px)',
        background: 'rgba(0,0,0,.66)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="sp-page-row">
        <RouteLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark />
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.035em' }}>stagepost</span>
        </RouteLink>
        <span
          style={{
            font: '500 11px var(--font-machine)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-faint)',
          }}
        >
          {label}
        </span>
        <div style={{ flex: 1 }} />
        {session === 'signed-in' && (
          <RouteLink to="/" ariaLabel="Back to your rooms" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-soft)' }}>
            <span aria-hidden="true" className="sp-wide-only">Back to your rooms</span>
            <span aria-hidden="true" className="sp-narrow-only">Rooms</span>
          </RouteLink>
        )}
        {session === 'signed-out' && (
          <PrimaryAction href="/#start" compact>
            Sign up free
          </PrimaryAction>
        )}
      </div>
    </header>
  )
}

function Closing() {
  return (
    <div className="sp-legal-section" style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
      <RouteLink to="/privacy" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent-bright)' }}>
        Privacy →
      </RouteLink>
      <RouteLink to="/terms" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent-bright)' }}>
        Terms →
      </RouteLink>
      <RouteLink to="/guide" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent-bright)' }}>
        Guide →
      </RouteLink>
    </div>
  )
}

function Footer(): ReactNode {
  return (
    <footer style={{ borderTop: '1px solid var(--border)', background: 'rgba(255,255,255,.015)' }}>
      <div className="sp-page-footer-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark size={20} color="var(--text-body)" />
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.03em', color: 'var(--text-body)' }}>
            stagepost
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <RouteLink to="/" style={{ fontSize: 13, color: 'var(--text-body)' }}>
          Home
        </RouteLink>
      </div>
    </footer>
  )
}

/**
 * `Legal` as an extension page. A page is handed only its path, so this one
 * asks the server who is reading, the same probe `App` makes, and paints while
 * the answer is on its way.
 */
export function LegalPage({ document: doc }: { document: 'privacy' | 'terms' }) {
  const { state } = useSession()
  return <Legal document={doc} session={state.status} />
}
